import { Button, Calendar, Space } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import CalendarEntriesLoader from "../../util/calendar-entries-loader";
import WorkTimesLoader from "../../util/work-times-loader";
import CalendarEntryModal from "./calendar-entry-modal";
import dayjs, { Dayjs } from "dayjs";
import generateConfig from 'rc-picker/lib/generate/dayjs';
import { useGetAbsencesQuery, useGetCalendarSeriesesQuery, useGetCustomerQuery } from "../../../../api/plamo-api";
import { usePrivileges } from "../../../oidc";

const CREATE_PRIVILEGE = 'plamo:calendar-series:rest:create-item';
const UPDATE_PRIVILEGE = 'plamo:calendar-entry:rest:update-item';

// Max. deviation (in minutes) of start and end between planned and booked time to count as a match
const MATCH_TOLERANCE_MINUTES = 15;
// Max. distance (in minutes) between a planned and a booked time that do not overlap to still be paired
const PAIRING_DISTANCE_MINUTES = 60;

type RowStatus = 'match' | 'deviation' | 'unbooked' | 'unplanned' | 'planned' | 'other';

const STATUS_STYLES: Record<RowStatus, { color: string, background: string, icon: string, title: string }> = {
  match: { color: '#52C41A', background: '#F6FFED', icon: '✓', title: 'Geplant und gebucht' },
  deviation: { color: '#FAAD14', background: '#FFFBE6', icon: '≈', title: `Geplant und gebucht, Abweichung über ${MATCH_TOLERANCE_MINUTES} Min.` },
  unbooked: { color: '#FF4D4F', background: '#FFF1F0', icon: '⚠', title: 'Geplant, aber nicht gebucht' },
  unplanned: { color: '#FA8C16', background: '#FFF2E8', icon: '✗', title: 'Gebucht ohne Planung' },
  planned: { color: '#8C8C8C', background: '#FAFAFA', icon: '○', title: 'Geplant (noch offen)' },
  other: { color: '#1677FF', background: '#E6F4FF', icon: '•', title: 'Andere Tätigkeit' },
};

const formatRange = (item: any) => `${dayjs(item.startTime).format('HH:mm')} - ${dayjs(item.endTime).format('HH:mm')}`;

const minutesBetween = (a: any, b: any) => Math.abs(dayjs(a).diff(dayjs(b), 'minute'));

const matchDay = (entries: any[], workTimes: any[], seriesById: Record<string, any>, now: Dayjs) => {
  const plans = entries.map(entry => ({ entry, customerId: seriesById[entry.calendarSeriesId]?.customerId }));
  const visits = workTimes.filter(w => !w.otherTask || w.customerId || w.substitute);

  const candidates: { plan: any, workTime: any, score: number }[] = [];
  plans.forEach(plan => visits.forEach(workTime => {
    // Customer must match if known on both sides
    if (plan.customerId && workTime.customerId && plan.customerId !== workTime.customerId) {
      return;
    }
    const overlaps = dayjs(plan.entry.startTime).isBefore(workTime.endTime) && dayjs(workTime.startTime).isBefore(plan.entry.endTime);
    const startDistance = minutesBetween(plan.entry.startTime, workTime.startTime);
    if (!overlaps && startDistance > PAIRING_DISTANCE_MINUTES) {
      return;
    }
    candidates.push({ plan, workTime, score: startDistance + minutesBetween(plan.entry.endTime, workTime.endTime) });
  }));
  candidates.sort((a, b) => a.score - b.score);

  const pairedPlans = new Set<any>();
  const pairedWorkTimes = new Set<any>();
  const rows: { status: RowStatus, entry?: any, workTime?: any, customerId?: any, sortTime: number }[] = [];

  candidates.forEach(({ plan, workTime }) => {
    if (pairedPlans.has(plan) || pairedWorkTimes.has(workTime)) {
      return;
    }
    pairedPlans.add(plan);
    pairedWorkTimes.add(workTime);
    const withinTolerance = minutesBetween(plan.entry.startTime, workTime.startTime) <= MATCH_TOLERANCE_MINUTES
      && minutesBetween(plan.entry.endTime, workTime.endTime) <= MATCH_TOLERANCE_MINUTES;
    rows.push({ status: withinTolerance ? 'match' : 'deviation', entry: plan.entry, workTime, customerId: plan.customerId || workTime.customerId, sortTime: dayjs(plan.entry.startTime).valueOf() });
  });

  plans.filter(plan => !pairedPlans.has(plan)).forEach(plan => {
    rows.push({ status: dayjs(plan.entry.endTime).isAfter(now) ? 'planned' : 'unbooked', entry: plan.entry, customerId: plan.customerId, sortTime: dayjs(plan.entry.startTime).valueOf() });
  });

  workTimes.filter(workTime => !pairedWorkTimes.has(workTime)).forEach(workTime => {
    rows.push({ status: visits.includes(workTime) ? 'unplanned' : 'other', workTime, customerId: workTime.customerId, sortTime: dayjs(workTime.startTime).valueOf() });
  });

  return rows.sort((a, b) => a.sortTime - b.sortTime);
}

const CustomerName = ({ customerId, workTime, series }: any) => {
  const { data: customer } = useGetCustomerQuery({ id: customerId }, { skip: !customerId }) as any;
  if (customer) {
    return <>{ customer.firstName } { customer.lastName }</>;
  }
  if (series && !series.customerId) {
    return <>{ series.substitutionCustomerFirstName } { series.substitutionCustomerLastName } (Vertretung)</>;
  }
  if (workTime?.substitute) {
    return <>{ workTime.substitutionCustomerFirstName } { workTime.substitutionCustomerLastName } (Vertretung)</>;
  }
  return <>{ workTime?.otherTask }</>;
}

const MatchRow = ({ row, series, onEdit }: any) => {
  const style = STATUS_STYLES[row.status as RowStatus];
  const content = <>
    <div><span style={{ color: style.color, fontWeight: 'bold' }}>{ style.icon }</span> <CustomerName customerId={row.customerId} workTime={row.workTime} series={series} /></div>
    { row.entry && <div style={{ textDecoration: row.status === 'unbooked' ? 'line-through' : undefined }}>Plan: { formatRange(row.entry) }{ series?.pattern && <span title="Serientermin"> ↻</span> }</div> }
    { row.workTime && <div>{ row.status === 'other' ? '' : 'Ist: ' }{ formatRange(row.workTime) }</div> }
  </>;
  const boxStyle = { position: 'relative' as const, backgroundColor: style.background, borderLeft: `3px solid ${style.color}`, padding: '0 4px', marginBottom: 2, fontSize: '0.85em', lineHeight: 1.3 };

  return <div title={style.title} style={boxStyle}>
    { row.workTime
      ? <Link to={`/worktime/${row.workTime.id}`} style={{ display: 'block', color: 'inherit' }}>{ content }</Link>
      : content }
    { onEdit && row.entry && <Button
      type="text"
      size="small"
      icon={<EditOutlined />}
      title="Termin bearbeiten"
      style={{ position: 'absolute', top: 0, right: 0 }}
      onClick={e => { e.stopPropagation(); onEdit(row.entry); }}
    /> }
  </div>;
}

const Legend = () => <Space wrap size="middle" style={{ marginBottom: 8 }}>
  { (Object.keys(STATUS_STYLES) as RowStatus[]).map(status => <span key={status}>
    <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: STATUS_STYLES[status].background, borderLeft: `3px solid ${STATUS_STYLES[status].color}`, marginRight: 4, verticalAlign: 'middle' }} />
    { STATUS_STYLES[status].title }
  </span>) }
</Space>

const LocalizedCalendar = Calendar.generateCalendar<Dayjs>(generateConfig);

function EmployeeCalendar({ employeeId }) {
  const { data: absences } = useGetAbsencesQuery({ f: JSON.stringify({ "employee.id": { c: "EQ", v: employeeId }}), st: JSON.stringify({ id: 'asc' }) }) as any
  const { data: calendarSerieses } = useGetCalendarSeriesesQuery({ o: 0, c: 1000, f: JSON.stringify({ "employee.id": { c: "EQ", v: employeeId }}) }) as any

  const seriesById = useMemo(() => Object.fromEntries((calendarSerieses?.items || []).map(s => [s.id, s])), [calendarSerieses]);
  const privileges = usePrivileges([CREATE_PRIVILEGE, UPDATE_PRIVILEGE]);
  const canCreate = !!privileges[CREATE_PRIVILEGE];
  const canUpdate = !!privileges[UPDATE_PRIVILEGE];

  // { date } to add a new entry, { entry } to edit an existing one
  const [modalState, setModalState] = useState<any>(null);
  const closeModal = useCallback(() => setModalState(null), []);
  const onEdit = useCallback(entry => setModalState({ entry }), []);

  const [calendarEntriesByMonth, setCalendarEntriesByMonth] = useState<any>({})
  const [workTimesByMonth, setWorkTimesByMonth] = useState<any>({})

  const [selectedMonthsJSON, setSelectedMonthsJSON] = useState<string>(JSON.stringify([dayjs().format('YYYY/MM')]));
  const selectedMonths = useMemo(() => JSON.parse(selectedMonthsJSON), [selectedMonthsJSON]);

  const getRows = useCallback((value: Dayjs) => {
    const monthKey = dayjs(value).format('YYYY/MM');
    const day = dayjs(value).format('YYYY-MM-DD');
    const entries = (calendarEntriesByMonth[monthKey] || []).filter(e => dayjs(e.startTime).format('YYYY-MM-DD') === day);
    const workTimes = (workTimesByMonth[monthKey] || []).filter(w => dayjs(w.startTime).format('YYYY-MM-DD') === day);
    return matchDay(entries, workTimes, seriesById, dayjs());
  }, [calendarEntriesByMonth, workTimesByMonth, seriesById]);

  const fullCellRender = useCallback((current: Dayjs, info) => {
    if (info.type === 'date') {
      const rows = getRows(current);
      const isVacation: boolean = absences?.items?.filter(v => v.reason === 'Urlaub').filter(v => {
        const currentAtMidnight = current.hour(0).minute(0).second(0).millisecond(0)
        return dayjs(v.begin?.replace('Z', '')).valueOf() <= currentAtMidnight.valueOf() && dayjs(v.end?.replace('Z', '')).valueOf() >= currentAtMidnight.valueOf()
      }).length > 0
      const isOtherAbsence: boolean = absences?.items?.filter(v => v.reason !== 'Urlaub').filter(v => {
        const currentAtMidnight = current.hour(0).minute(0).second(0).millisecond(0)
        return dayjs(v.begin?.replace('Z', '')).valueOf() <= currentAtMidnight.valueOf() && dayjs(v.end?.replace('Z', '')).valueOf() >= currentAtMidnight.valueOf()
      }).length > 0
      return <div className="ant-picker-cell-inner ant-picker-calendar-date" style={{ backgroundColor: isOtherAbsence ? '#FFAAAA' : (isVacation ? '#AAFFAA' : undefined) }}>
        <div className="ant-picker-calendar-date-value" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          { canCreate
            ? <Button type="text" size="small" icon={<PlusOutlined />} title="Termin hinzufügen" onClick={e => { e.stopPropagation(); setModalState({ date: current.startOf('day') }); }} />
            : <span /> }
          { current.format('DD') }
        </div>
        <div className="ant-picker-calendar-date-content">
          { rows.map(row => <MatchRow key={`${row.entry?.id}-${row.workTime?.id}`} row={row} series={seriesById[row.entry?.calendarSeriesId]} onEdit={canUpdate ? onEdit : undefined} />) }
        </div>
      </div>
    }
    return info.originNode;
  }, [getRows, absences, seriesById, canCreate, canUpdate, onEdit])

  const onPanelChange = useCallback(date => {
    const monthKey = date.format('YYYY/MM');
    setSelectedMonthsJSON((selectedMonths: string) => JSON.stringify([...JSON.parse(selectedMonths).filter(k => k !== monthKey), monthKey].sort()))
  }, [setSelectedMonthsJSON])

  return <>
    <CalendarEntriesLoader employeeId={employeeId} months={selectedMonths} onEntriesLoaded={setCalendarEntriesByMonth} />
    <WorkTimesLoader employeeId={employeeId} months={selectedMonths} onWorkTimesLoaded={setWorkTimesByMonth} />
    <Legend />
    <LocalizedCalendar onPanelChange={onPanelChange} fullCellRender={fullCellRender} />
    <CalendarEntryModal employeeId={employeeId} entry={modalState?.entry} series={seriesById[modalState?.entry?.calendarSeriesId]} date={modalState?.date} onClose={closeModal} />
  </>;
}

export default EmployeeCalendar;
